<template>
<div>

  <nav class="flex flex-wrap items-center text-sm mb-3">
    <router-link :to="'/admin/validations/'"
      class="flex items-center text-blue hover:text-blue-darker hover:underline">
      <i class="fas fa-check-double mr-1"></i>{{ $t('validations') }}
    </router-link>

    <i class="fas fa-chevron-right text-grey text-xs mx-2"></i>

    <router-link :to="'/admin/validation-template/'+ templateId +'/edit'"
      class="flex items-center font-semibold text-blue hover:text-blue-darker hover:underline truncate"
      style="max-width:20rem"
      :title="templateName">
      <i class="fas fa-project-diagram mr-1"></i>{{ templateName }}
    </router-link>

    <i class="fas fa-chevron-right text-grey text-xs mx-2"></i>

    <span class="text-grey-dark">{{ $t('tasks') }}</span>

    <i class="fas fa-chevron-right text-grey text-xs mx-2"></i>

    <span class="rounded-full bg-blue-lightest border border-blue-lighter text-blue-darker font-bold px-3 py-0.5 truncate"
      style="max-width:24rem"
      :title="form.code + ' ' + form.name">
      <span class="text-blue-dark mr-1">{{ form.code }}</span>{{ form.name }}
    </span>
  </nav>


  <div class="panel">

    <!-- Datos de la tarea -->
    <h3 class="text-sm font-bold uppercase tracking-wide text-grey-darker border-b border-grey-light pb-2 mb-4">
      {{ $t('details') }}
    </h3>

    <div class="grid grid-cols-12 gap-4 items-end">
      <div class="col-span-3">
        <l-form-input ref="taskCode"
          :form="form"
          field="code"
          label="code"
        />
      </div>
      <div class="col-span-6">
        <l-form-input ref="taskName"
          :form="form"
          field="name"
          label="name"
        />
      </div>
      <div class="col-span-1">
        <l-form-input ref="taskOrder"
          :form="form"
          field="order_column"
          label="order"
        />
      </div>
      <div class="col-span-2 pb-3">
        <b-tooltip :label="$t('concurrent_child_tasks')" position="is-left" v-show="form.parent_id===null">
          <b-switch v-model="form.is_concurrent" type="is-success" passive-type="is-danger">
            {{ $t('concurrent') }}
          </b-switch>
        </b-tooltip>
      </div>
    </div>

    <!-- Descripción -->
    <div class="mt-4">
      <l-form-input
        :form="form"
        field="description"
        label="description"
        input-type="textarea"
        :maxlength="2000"
        class="w-full"
      />
    </div>


    <!-- Grupos -->
    <div class="flex items-center justify-between border-b border-grey-light pb-2 mt-8 mb-4">
      <h3 class="text-sm font-bold uppercase tracking-wide text-grey-darker">
        {{ $t('groups') }}
      </h3>

      <!-- Grupo inicial (manager_group_id): lo actualiza el backend al poner un grupo en orden 0 -->
      <div class="flex items-center text-sm">
        <span class="text-grey-darker mr-2">
          <i class="fas fa-flag text-green-dark mr-1"></i>{{ $t('initial_group') }}
        </span>
        <span class="rounded-full bg-green-lightest border border-green-light text-green-darker font-bold px-3 py-0.5">
          {{ managerGroup ? managerGroup.code : '-' }}
        </span>
        <span v-if="managerGroupId" class="text-xs text-grey-dark ml-2">#{{ managerGroupId }}</span>
      </div>
    </div>

    <draggable
      v-model="form.groups"
      :group="{ name: 'groups' }"
      :animation="200"
      @end="reorderTaskGroups"
      tag="div"
      class="grid grid-cols-4 gap-4"
      :filter="'.non-draggable'"
      :prevent-on-filter="false"
    >
      <!-- recuadro grupo. El primero (orden 0) es el grupo inicial de la tarea -->
      <div v-for="(group, index) in form.groups" :key="group.id"
        class="flex flex-col rounded-lg shadow-sm hover:shadow-md transition-shadow p-3 cursor-move"
        :class="index === 0 ? 'bg-green-lightest border-2 border-green' : 'bg-blue-lightest border border-blue-lighter'">

        <!-- franja superior de color -->
        <div class="h-1 -mx-3 -mt-3 mb-3 rounded-t-lg" :class="index === 0 ? 'bg-green' : 'bg-blue-light'"></div>

        <div class="flex items-center">
          <i class="fas fa-grip-vertical text-grey mr-2"></i>
          <span class="text-xl font-bold text-blue-darker truncate flex-grow">{{ group.group.code }}</span>

          <span class="rounded-full bg-white border border-blue-lighter text-blue-dark text-xs font-semibold px-2 py-0.5 ml-2 whitespace-nowrap">
            {{ $t('order') }} {{ group.order_column }}
          </span>
          <!-- <input v-model="group.order_column" type="text" style="width:40px;"> -->

          <b-tooltip :label="$t('unlink')" position="is-left" type="is-danger" class="ml-2">
            <span class="text-grey-dark hover:text-red cursor-pointer" @click="unlinkGroup(group.id, group.group.code)">
              <i class="fas fa-times-circle"></i>
            </span>
          </b-tooltip>
        </div>

        <div v-if="index === 0" class="mt-2">
          <span class="inline-flex items-center rounded bg-green text-white text-xs font-semibold px-2 py-0.5">
            <i class="fas fa-flag mr-1"></i>{{ $t('initial_group') }}
          </span>
        </div>

        <div class="text-xs uppercase tracking-wide text-grey-dark mt-3 mb-1">{{ $t('participants') }}</div>
        <ul>
          <li
            v-for="(user) in groupUsers(group.group.code)"
            :key="user.user_id"
            class="flex items-center rounded bg-white border border-blue-lighter px-2 py-1 mb-1">
            <img :src="user.basic.avatar"
              class="rounded-full overflow-hidden flex-shrink-0"
              style="width:24px;height:24px">
            <span class="text-xs font-semibold text-blue-dark truncate pl-2">
              {{ user.basic.fullname }}
            </span>
          </li>
          <li v-if="!groupUsers(group.group.code).length" class="text-xs text-grey-dark">-</li>
        </ul>

      </div>

      <!-- recuadro añadir grupo -->
      <div v-if="unassignedTemplateGroups.length"
        class="non-draggable flex flex-col justify-center rounded-lg border-2 border-dashed border-blue-lighter bg-grey-lightest p-3 text-center">

        <span class="font-bold text-blue">{{ $t('add_group') }}</span>

        <b-select v-model="groupToAdd" :placeholder="$t('select')" expanded class="my-2">
          <option v-for="group in unassignedTemplateGroups" :value="group" :key="'group_'+group.id">
            {{ group.code }}
          </option>
        </b-select>

        <span class="text-xs text-grey-darker">{{ $t('order') }}: {{ groupToAddOrder }}</span>
        <!-- <input v-model="groupToAddOrder" type="text" style="width:40px;"> -->

        <button class="btn btn-green items-center mt-2" style="margin:auto" type="button" @click="linkGroup()">
          <i class="fas fa-plus mr-1"></i> {{ $t('add_group') }}
        </button>

      </div>
    </draggable>

    <div class="flex items-start rounded bg-red-lightest text-red-dark text-xs p-2 mt-4">
      <i class="fas fa-exclamation-triangle mr-2 mt-0.5"></i>
      <span>{{ $t('validation_template_task_warning') }}</span>
    </div>

  </div>

  <form @submit.prevent="sendTaskData" @keydown="form.onKeydown($event)"
    class="w-full flex items-center justify-end mb-8">
    <v-button :disabled="disableSaveButton" type="submit" icon="thumbs-up" icon-pack="far" class="mr-4">{{ $t('save') }}</v-button>
    <v-button color="grey" icon="undo-alt" @click="$router.go(-1)">{{ $t('cancel') }}</v-button>
  </form>

</div>
</template>

<script>
import axios from 'axios'
import Form from 'vform'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'

export default {
  middleware: 'auth',
  title: 'validations_edit_page_title',
  subtitle: 'validations_edit_page_subtitle',
  menuOption: '0-5',

  components: {
    draggable,
  },

  data: () => ({
    templateId: null,
    templateName: '', // solo para el breadcrumb
    taskId: null,
    templateFlow: {}, // objecto template padre de esta tarea
    form: new Form({
      code: '',
      name: '',
      description: '',
      order_column: 0,
      groups: [],
      original_groups: [],//almacena los grupos originales antes de ser movidos de orden con draggable
      is_concurrent: false,
    }),
    // grupo inicial de la tarea, fuera del form para que guardar la tarea no lo sobrescriba
    managerGroupId: null,
    managerGroup: null,
    disableSaveButton: false,
    unassignedTemplateGroups: [], // plantillas de grupos posibles a añadir al template
    groupToAdd: {}, // el grupo elegido para añadir
    groupToAddOrder: 0, // el orden en el que irá el nuevo grupo
  }),

  created() {

    this.templateId = this.$route.params.validation_id
    this.taskId = this.$route.params.task_id

  },

  computed: {
    ...mapGetters({
      company: 'app/company',
    }),
  },

  mounted() {
    this.getTaskData()
    this.getTemplateData()
    this.$refs.taskCode.setFocus()
  },

  methods: {

    async reorderTaskGroups() {
      this.form.groups.forEach((group, index) => {
        group.order_column = index
      })
      // buscamos los grupos que han cambiado de orden 
      let updatedGroups = this.form.groups.filter(g => {
        return this.form.original_groups.some(originalGroup => originalGroup.id === g.id && originalGroup.order_column !== g.order_column)
      })

      if (!updatedGroups.length) {
        return
      }

      // guardamos los grupos que han cambiado de orden y recargamos una sola vez al terminar todos,
      // para que el grupo inicial mostrado sea el que dejó el backend tras el último guardado
      const results = await Promise.all(updatedGroups.map(group => this.editGroup(group)))

      await this.getTaskData()

      if (results.every(result => result)) {
        this.$notify.success('success_editing')
      }
    },

    async getTaskData() {
 
      const { data } = await axios.get('/api/v2/validations/template/task/' + this.taskId)

      if (data && data.success) {

        this.form.code = data.data.code
        this.form.name = data.data.name
        this.templateName = data.data.flow.name
        this.form.description = data.data.description
        this.form.order_column = data.data.order_column
        this.form.groups = data.data.groups
        this.form.original_groups = JSON.parse(JSON.stringify(data.data.groups)) // copia profunda para mantener el orden original
        this.form.is_concurrent = !!data.data.is_concurrent // b-switch espera booleano y la api devuelve 1/0
        this.form.parent_id = data.data.parent_id
        this.managerGroupId = data.data.manager_group_id
        this.managerGroup = data.data.manager_group

        // recalcula los grupos que quedan por añadir. Solo si ya hemos cargado los datos de la plantilla padre
        if (this.templateFlow.id) {
          this.unassignedTemplateGroups = this.templateFlow.groups;
        }
        this.groupToAdd = {}
        this.groupToAddOrder = this.getMaxOrder(this.form.groups) + 1

      } else if(data && data.msg){

        this.$notify.error('no_access_permissions')
        this.$router.push({ name: 'project.home', params: {} })

      } else {
        this.$notify.error('error_retrieving_data')
      }
    },


    async getTemplateData() {
      const { data } = await axios.get('/api/v2/validations/template/' + this.templateId + '?withUsers=1')
      if (data && data.success) {

        this.templateFlow = data.data
        
        this.unassignedTemplateGroups = this.templateFlow.groups;

        this.groupToAddOrder = this.getMaxOrder(this.form.groups) + 1

      } else if(data && data.msg){

        this.$notify.error('no_access_permissions')
        this.$router.push({ name: 'project.home', params: {} })

      } else {
        this.$notify.error('error_retrieving_data')
      }
    },


    async sendTaskData() {
      var url = '/api/v2/validations/template/task/' + this.taskId;
      this.disableButton();

      // Enviamos el form
      const { data } = await this.form.post(url)
      if (data && data.success) {

        this.$notify.success('success_editing')
        this.$router.push({ name: 'validation-template.edit', params: { validation_id: this.templateId } })

      } else if(data && data.msg){

        this.$notify.error('no_access_permissions')
        this.$router.push({ name: 'project.home', params: {} })

      } else {
        this.$notify.error('error_saving')
      }
    },


    // vincula el grupo seleccionado con la tarea
    async linkGroup() {
      if (!this.groupToAdd.id || isNaN(this.groupToAddOrder)) {
        this.$notify.error('Debe seleccionar un grupo de la lista y establecer su orden')
        return
      }

      const { data } = await axios.post("/api/v2/validations/templatetaskgroup", {
        company_id: this.company.id,
        task_id: this.taskId,
        group_code: this.groupToAdd.code,
        // status: this.groupToAdd.code, se mete en backend default 0
        order_column: this.groupToAddOrder,
      })
      if (data && data.success) {

        // reload
        this.getTaskData()
        
        this.$notify.success('success_editing')

      } else if(data && data.msg){

        this.$notify.error('no_access_permissions')

      } else {
        this.$notify.error('error_saving')
      }
    },


    // desvincula el grupo de esta tarea
    async unlinkGroup(groupId, groupName) {

      let self = this;
      this.$dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('delete_generic', [groupName]),
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {

          const { data } = await axios.post("/api/v2/validations/templatetaskgroup/" + groupId + "/delete", { })
          if (data && data.success) {

            // reload
            this.getTaskData()
            this.$notify.success('success_deleting_generic')

          } else if(data && data.msg){

            this.$notify.error('no_access_permissions')

          } else {
            this.$notify.error('error_saving')
          }
        }
      })
    },


    // edita el grupo (orden y poco mas). Devuelve true si se guardó; la recarga la hace quien llama
    async editGroup(group) {

      if (isNaN(parseInt(group.order_column))) {
        this.$notify.error('Debe establecer un orden numerico para el grupo ' + group.group.code)
        return false
      }

      let url = '/api/v2/validations/templatetaskgroup/' + group.id;

      try {
        const { data } = await axios.post(url, { order_column: group.order_column })
        if (data && data.success) {
          return true
        }

        if (data && data.msg) {
          this.$notify.error('no_access_permissions')
        } else {
          this.$notify.error('error_saving')
        }
      } catch (error) {
        this.$notify.error('error_saving')
      }

      return false
    },


    // devuelve el contenido de usuarios del grupo pasado (usuarios estan en grupo de la plantilla del flujo no en el grupo de la tarea)
    groupUsers(code) {

      if (!this.templateFlow || !this.templateFlow.groups)
        return []

      const group = this.templateFlow.groups.find(g => g.code === code)

      return group && Array.isArray(group.users)
        ? group.users
        : []
    },


    // a los grupos de la plantilla le restamos los de la tarea y esos son los que podrá añadir
    // array diff, devuelve los templateGroups que no estan en taskGroup
    groupDiff(templateGroups, taskGroups) {

      return templateGroups.filter(
              templateGroup => !taskGroups.some(taskGroup => templateGroup.code == taskGroup.group.code)
            )

    },


    // obtiene el maximo orden de los grupos de la tarea
    getMaxOrder(groups) {

      let maxGroup = groups.reduce( function(maxValue, currentGroup) {
          return { order_column: Math.max(maxValue.order_column, currentGroup.order_column) }
        }
      )
      return maxGroup.order_column

    },


    disableButton() {
      this.disableSaveButton = true
      let self = this
      setTimeout(function() {
        self.disableSaveButton = false
      }, 2000);
    }

  },
};
</script>
